import PropTypes, { ReactPropTypeLocationNames } from 'prop-types';
import constants from 'utils/constants';

export const customPropValidators = {
    // see: https://www.ian-thomas.net/custom-proptype-validation-with-react/
    createChainableValidator: (validator) => {
        function checkType(isRequired, props, propName, componentName, location) {
            componentName = componentName || 'ANONYMOUS';
            if (props[propName] === null || props[propName] === undefined) {
                const locationName = ReactPropTypeLocationNames[location];
                if (isRequired) {
                    return new Error(`Required ${locationName}: ${propName} was not specified in ${componentName}`);
                }
                return null;
            }
            return validator(props, propName, componentName, location);
        }

        const chainedCheckType = checkType.bind(null, false);
        chainedCheckType.isRequired = checkType.bind(null, true);
        return chainedCheckType;
    },
    objectWithNumericKeys: (props, propName, componentName) => {
        if (Object.keys(props[propName]).some((key) => isNaN(key))) {
            return new Error(`Invalid prop ${propName} supplied to ${componentName}: must be numeric.`);
        }
    },
};

const propTypes = (() => {
    const types = {};

    types.user = PropTypes.shape({
        access_token: PropTypes.string,
        bk_user_id: PropTypes.number,
        display_picture: PropTypes.string,
        name: PropTypes.string,
        preferences: PropTypes.shape({
            default_country: PropTypes.number,
        }),
        user_preferences: PropTypes.shape({
            countries: PropTypes.arrayOf(PropTypes.number),
        }),
        source_id: PropTypes.string,
        source_type: PropTypes.string,
        username: PropTypes.string,
    });

    types.history = PropTypes.object;
    types.location = PropTypes.object;
    types.match = PropTypes.object;

    types.searchFieldsArray = PropTypes.arrayOf(PropTypes.oneOf(constants.SearchFilters));

    types.searchFilter = PropTypes.oneOf(['any', 'sync', 'conflicts', 'claims', 'mentions']);

    types.conflicting_territories = PropTypes.oneOfType([
        PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.number,
                name: PropTypes.string,
            }),
        ),
        PropTypes.arrayOf(PropTypes.number),
        PropTypes.shape({
            mechanical: PropTypes.array,
            performance: PropTypes.array,
        }),
    ]);

    types.metadata = PropTypes.shape({
        bk_work_id: PropTypes.number,
        catalogue_type: PropTypes.string,
        claims: types.claims,
        conflicting_territories: types.conflicting_territories,
        linked_recordings: PropTypes.array,
        iswcs: PropTypes.arrayOf(PropTypes.string),
        mentioners: PropTypes.array,
        sid: PropTypes.string,
        stateOnly: PropTypes.bool,
        titles: PropTypes.array,
        writers: PropTypes.arrayOf(PropTypes.object),
    });

    types.workOther = PropTypes.shape({
        publishers: PropTypes.objectOf(
            PropTypes.arrayOf(
                PropTypes.shape({
                    ipi: PropTypes.number,
                    name: PropTypes.string,
                }),
            ),
        ),
        writers: PropTypes.objectOf(
            PropTypes.arrayOf(
                PropTypes.shape({
                    ipi: PropTypes.number,
                    name: PropTypes.string,
                }),
            ),
        ),
    });

    types.work = PropTypes.shape({
        activity: types.activity,
        metadata: types.metadata,
        conflicting_territories: types.conflicting_territories,
        source: PropTypes.object, // TODO https://github.com/airbnb/prop-types/tree/master/src
        state: PropTypes.object, // TODO
        other: types.workOther,
    });

    types.summary = PropTypes.shape({
        conflicts: PropTypes.number,
        mentions: PropTypes.number,
        total: PropTypes.number,
        claims: PropTypes.number,
    });

    types.suggestedActions = PropTypes.arrayOf(
        PropTypes.shape({
            deadline: PropTypes.string,
            popularity: PropTypes.number,
            type: PropTypes.oneOf(['conflicts', 'claims']),
            value: PropTypes.oneOf(['high', 'medium', 'low']),
            work: PropTypes.shape({
                bk_work_id: PropTypes.number,
                titles: PropTypes.arrayOf(PropTypes.string),
            }),
        }),
    );

    types.share = PropTypes.shape({
        name: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        ipi: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
        pr_society: PropTypes.number,
        pr_society_name: PropTypes.string,
        pr_share: PropTypes.number,
        mr_society: PropTypes.number,
        mr_society_name: PropTypes.string,
        mr_share: PropTypes.number,
    });

    types.conflict = types.share;

    types.conflicts = PropTypes.shape({
        publisher_shares: PropTypes.arrayOf(types.conflict),
        writer_shares: PropTypes.arrayOf(types.conflict),
    });

    types.territory = PropTypes.shape({
        code: PropTypes.number,
        name: PropTypes.string,
        writer_shares: PropTypes.arrayOf(types.share),
        publisher_shares: PropTypes.arrayOf(types.share),
    });

    types.claimResponses = PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            claim_id: PropTypes.number,
            created: PropTypes.string,
            from: PropTypes.oneOfType([PropTypes.number, types.user]),
            to: PropTypes.oneOfType([PropTypes.number, types.user]),
            comment: PropTypes.string,
            files: PropTypes.arrayOf(PropTypes.object),
            accepted: PropTypes.bool,
            expires: PropTypes.string,
            open: PropTypes.bool,
        }),
    );

    types.claim = PropTypes.shape({
        id: PropTypes.number,
        bk_work_id: PropTypes.number,
        created: PropTypes.string,
        claimant: PropTypes.oneOfType([PropTypes.number, types.user]),
        defendants: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.arrayOf(types.user)]),
        state: PropTypes.string,
        responses: types.claimResponses,
    });

    types.hover = PropTypes.shape({
        ipi: PropTypes.number,
        entityType: PropTypes.number,
        shareType: PropTypes.number,
    });

    types.upload = PropTypes.shape({
        modalOpen: PropTypes.bool,
    });

    types.activityItemMeta = PropTypes.oneOfType([
        PropTypes.shape({
            iswc: PropTypes.string,
            title: PropTypes.string,
        }),
        PropTypes.shape({
            existing: PropTypes.bool,
            ipi: PropTypes.number,
            name: PropTypes.string,
            props: PropTypes.arrayOf(
                PropTypes.shape({
                    country: PropTypes.string,
                    country_id: PropTypes.number,
                    mr_share: PropTypes.number,
                    mr_society: PropTypes.number,
                    pr_share: PropTypes.number,
                    pr_society: PropTypes.number,
                }),
            ),
        }),
        PropTypes.shape({
            claimant: PropTypes.shape({
                name: PropTypes.string,
                source_id: PropTypes.string,
                source_type: PropTypes.string,
            }),
            defendants: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string,
                    source_id: PropTypes.string,
                    source_type: PropTypes.string,
                }),
            ),
            publishers: PropTypes.arrayOf(
                PropTypes.shape({
                    country_shares: PropTypes.arrayOf(
                        PropTypes.shape({
                            country: PropTypes.string,
                            country_id: PropTypes.number,
                            mr_share: PropTypes.number,
                            mr_society: PropTypes.number,
                            pr_share: PropTypes.number,
                            pr_society: PropTypes.number,
                            sid: PropTypes.string,
                        }),
                    ),
                    ipi: PropTypes.number,
                    name: PropTypes.string,
                }),
            ),
        }),
    ]);

    types.activityItem = PropTypes.shape({
        action: PropTypes.number,
        created_at: PropTypes.string,
        meta: PropTypes.oneOfType([PropTypes.arrayOf(types.activityItemMeta), types.activityItemMeta]),
        version: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    });

    types.activity = PropTypes.shape({
        nextOffset: PropTypes.number,
        items: PropTypes.arrayOf(types.activityItem),
    });

    return types;
})();

export default propTypes;
